import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CategoryCard from "@/components/CategoryCard";
import ComingSoonCard from "@/components/ComingSoonCard";
import { MODEL_FILTERS, YEAR_FILTERS } from "@/data/categories";
import { SEO_PAGE_LINKS } from "@/data/seoPages";
import { useCategories } from "@/hooks/useCategories";
import { useSettings } from "@/hooks/useSettings";
import { useSEO } from "@/hooks/useSEO";

export default function Catalog() {
  const [model, setModel] = useState("all");
  const [year, setYear] = useState("all");
  const meta = useCategories();
  const settings = useSettings();

  useSEO({
    title: "ტაქსის მანქანების კატალოგი ქირით — Prius, Tesla, BYD | taxipark.ge",
    description: "ტაქსის მანქანები ქირით თბილისში: Toyota Prius 2013–2022, Tesla Model 3 და Model Y, BYD Qin Plus. დაადარე ფასები, დეპოზიტები და პირობები, აირჩიე და დაჯავშნე.",
    path: "/catalog",
  });

  const all = useMemo(
    () => Object.values(meta).filter((c) => !c.hidden).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
    [meta]
  );

  const filtered = useMemo(
    () =>
      all.filter(
        (c) => (model === "all" || c.model === model) && (year === "all" || c.years === year)
      ),
    [all, model, year]
  );

  return (
    <main data-testid="catalog-page" className="pt-28 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">ავტოპარკი</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">ავტომობილების კატეგორიები</h1>
        <p className="text-lg text-gray-400 mt-4 max-w-2xl leading-relaxed" data-testid="catalog-intro">
          {settings.catalog_intro || "აირჩიე კატეგორია და იხილე ყველა რეალური ავტომობილი მასში — ზუსტი წლით, ფასითა და ხელმისაწვდომობით."}
        </p>
        <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm" data-testid="catalog-guides" aria-label="სასარგებლო გვერდები">
          <span className="text-gray-500">გზამკვლევები:</span>
          {SEO_PAGE_LINKS.map((l) => (
            <Link key={l.path} to={l.path} className="text-gray-300 hover:text-[#E5C378] underline-offset-4 hover:underline transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="mt-10 space-y-4" data-testid="catalog-filters">
          <div className="flex flex-wrap gap-2">
            {MODEL_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-model-${f.id}`}
                onClick={() => setModel(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                  model === f.id
                    ? "bg-[#D4AF37] text-[#0A0B0E] border-[#D4AF37]"
                    : "bg-white/5 text-gray-300 border-white/15 hover:border-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {YEAR_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-year-${f.id}`}
                onClick={() => setYear(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                  year === f.id
                    ? "bg-[#D4AF37] text-[#0A0B0E] border-[#D4AF37]"
                    : "bg-white/5 text-gray-300 border-white/15 hover:border-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-8" data-testid="catalog-count">
          ნაპოვნია {filtered.length} კატეგორია
        </p>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-6" data-testid="catalog-grid">
            {filtered.map((cat) => (
              <CategoryCard key={cat.id} category={cat} meta={cat} />
            ))}
            {model === "all" && year === "all" && settings.soon_enabled !== false && (
              <ComingSoonCard settings={settings} />
            )}
          </div>
        ) : (
          <div className="card-lux p-12 text-center mt-6" data-testid="catalog-empty">
            <p className="text-gray-400">ამ ფილტრით კატეგორია ვერ მოიძებნა — სცადე სხვა კომბინაცია.</p>
          </div>
        )}
      </div>
    </main>
  );
}
